const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CAMP_NO_REFUND_WINDOW_DAYS = 14;

export const SUMMER_CAMP_POLICY = {
  supportEmail: 'billing@bhcskids.com',
  supportEmails: {
    programs: 'info@bhcskids.com',
    billing: 'billing@bhcskids.com',
  },
  noRefundWindowDays: CAMP_NO_REFUND_WINDOW_DAYS,
  cancellationTiers: [
    '100% refund when cancellation is 6 weeks or more before the camp week start date',
    '75% refund when cancellation is 4 to 5 weeks before the camp week start date',
    '50% refund when cancellation is 2 to 3 weeks before the camp week start date',
    'No refund, school credit, or camp switches within 2 weeks of your camp start date',
  ],
  registrationNotice:
    'Registration is complete only after payment is received.',
} as const;

/**
 * The cancellation tiers above are a summer-camp policy: they talk in weeks before the camp
 * week starts, which is meaningless for the single-day and break camps that share
 * `type = 'camp'`. Summer programs are tagged in admin with a "<year> Summer Camp" tag
 * (e.g. "2026 Summer Camp"), alongside conventions like "2026 Spring Camp" — match on the
 * tag so only summer camps show the policy.
 *
 * The same tag gates the multi-week session picker in ProgramDetails: only a tagged summer
 * program belongs to a set of sibling weeks. Returns the matched tag so callers can scope
 * queries to that one summer (e.g. "2026 Summer Camp") rather than every summer ever run.
 */
export function getSummerCampTag(
  program: { type?: string | null; tags?: string[] | null } | null | undefined,
): string | null {
  if (!program || program.type !== 'camp') return null;
  return (program.tags ?? []).find((tag) => /summer\s*camp/i.test(tag)) ?? null;
}

export function isSummerCampProgram(
  program: { type?: string | null; tags?: string[] | null } | null | undefined,
): boolean {
  return getSummerCampTag(program) !== null;
}

function parseDateOnly(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function daysUntilDate(dateString: string | null | undefined): number | null {
  if (!dateString) return null;
  const target = parseDateOnly(dateString);
  if (Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((target.getTime() - today.getTime()) / MS_PER_DAY);
}

export function isWithinCampNoRefundWindow(dateString: string | null | undefined): boolean {
  const daysUntil = daysUntilDate(dateString);
  return daysUntil !== null && daysUntil <= CAMP_NO_REFUND_WINDOW_DAYS;
}
